import { z } from "zod";
import { ACKNOWLEDGE_SIGNALS } from "./features.js";
import { LAB_STRATEGY_IDS } from "./lab-strategies.js";

/** A comma-separated URL parameter, which is how a list survives a bookmark. */
const csv = <T extends z.ZodTypeAny>(item: T) =>
  z
    .union([z.string(), z.array(z.string())])
    .transform((value) => (Array.isArray(value) ? value : value.split(",")))
    .pipe(z.array(item));

/**
 * Filters and sort live in the URL, validated here.
 *
 * Typed end to end, shareable, and the back button works. Unknown keys are
 * dropped rather than rejected: a stale bookmark must still load the page.
 */
export const screenSearchSchema = z
  .object({
    sort: z.string().optional(),
    dir: z.enum(["asc", "desc"]).optional(),
    factors: csv(z.string()).optional(),
    minTurnover: z.coerce.number().nonnegative().optional(),
    series: csv(z.enum(["EQ", "BE"])).optional(),
    sector: z.string().optional(),
    q: z.string().optional(),
  })
  // Unknown keys fall away instead of failing the navigation.
  .strip();

export type ScreenSearch = z.infer<typeof screenSearchSchema>;

export const labSearchSchema = z.object({ session: z.string().optional() }).strip();
export type LabSearch = z.infer<typeof labSearchSchema>;

/**
 * Every signal filter lives in the URL, so a filtered view is shareable and
 * the back button undoes a filter rather than leaving the page.
 *
 * `acknowledged` is three states, not a boolean: absent means both. Coercing
 * it would make `?acknowledged=false` and an absent parameter the same. It is
 * emptied while `ACKNOWLEDGE_SIGNALS` is off; the three states are what it
 * parses again the moment that flips.
 */
export const signalsSearchSchema = z
  .object({
    // The URL validator behind `SignalsSearch`. A family missing here renders
    // its chip, writes its query string, and gets dropped on the way back in —
    // a filter that visibly does nothing. See the list in
    // `apps/api/src/routes/signals.ts`.
    family: z.enum(["rank", "technical", "event", "intraday", "swing"]).optional(),
    minSeverity: z.enum(["info", "warn", "urgent"]).optional(),
    symbol: z.string().optional(),
    signalType: z.string().optional(),
    // The Size filter's bands and index chips, comma-separated like
    // `signalType`. A plain string rather than an enum for the same reason the
    // route takes one: an unrecognised band is dropped where the query is
    // built, so a bookmark saved before a band was renamed still loads the
    // page instead of failing the navigation.
    marketCap: z.string().optional(),
    // Which way the signal points, or `none` for the rows that point no way
    // at all — rank decile crossings and series changes, which were 1,411 of
    // 6,766 rows over a 5-session prod window. An absent parameter means every
    // row, including those, so "none" is a selection rather than the way to
    // spell unfiltered.
    //
    // An enum rather than the free string `marketCap` is, and for the opposite
    // reason: a band can be renamed, so a stale bookmark naming one has to
    // load the page. These three are closed. A URL naming a fourth is a typo.
    //
    // `.catch` rather than a bare enum, which is what makes that typo land on
    // the unfiltered feed with the chip showing Any instead of on the router's
    // error page. `validateSearch` in `router.tsx` calls `.parse`, so a
    // rejected value is a navigation that fails rather than a filter that is
    // dropped — the page the reader wanted, minus one narrowing, is the more
    // useful answer to a URL they cannot see.
    side: z.enum(["long", "short", "none"]).optional().catch(undefined),
    // Dropped rather than parsed while `ACKNOWLEDGE_SIGNALS` is off. This is
    // the one place the parameter enters the app, so emptying it here is what
    // keeps a hand-typed `?acknowledged=no` from filtering a feed that has no
    // control to undo it — and it empties the chip in `activeFilters`, the
    // cache key, and the request at the same time, with no second guard.
    acknowledged: z
      .enum(["yes", "no"])
      .optional()
      .transform((value) => (ACKNOWLEDGE_SIGNALS ? value : undefined)),
    // How many of the newest sessions the feed covers. Absent is the default
    // window, and `0` is every session — "all" has to survive a bookmark, and
    // an absent parameter cannot say it without meaning two different things.
    // No ceiling here for the same reason `limit` has none: a hand-edited
    // number is clamped in `feedSessions` rather than rejected, because a
    // stale bookmark should still load the page.
    sessions: z.coerce.number().int().nonnegative().optional(),
    // The timeline scrubber's selection, as two ISO instants. Plain strings
    // rather than `z.string().datetime()`: these are hand-editable and they
    // arrive from bookmarks, and a rejected parse would drop the reader on a
    // page that will not load at all. `timeWindow` in
    // `components/signals/signalTimeWindow.ts` reads an unparseable stamp as
    // "no bound", which loses the window and keeps the feed.
    //
    // Not on the `FILTERS` list in `routes/signals.tsx`, because this one
    // narrows rows already fetched. Resetting the depth for it would throw
    // away the very rows the scrubber is selecting from.
    from: z.string().optional(),
    to: z.string().optional(),
    // The header filters, in the compact form `columnFilterCodec.ts` reads:
    // `side:long;r:1..`. A plain string for the reasons `from` is one, and
    // not on the `FILTERS` list either: these narrow rows already fetched, so
    // a change must not reset the depth the reader has paged to. Nor is it in
    // the query key, which is how a menu click costs no round trip.
    cf: z.string().optional(),
    // The table's sort travels with the filters, so a ranked view is one link
    // rather than a filter link plus a header click.
    sort: z.string().optional(),
    dir: z.enum(["asc", "desc"]).optional(),
    // How deep the reader has asked to go. In the URL with everything else, so
    // "load more, then send this to me" survives the copy. The ceiling is the
    // route's; a larger number is clamped there rather than rejected here,
    // because a hand-edited URL should still load the page.
    limit: z.coerce.number().int().positive().optional(),
  })
  .strip();
export type SignalsSearch = z.infer<typeof signalsSearchSchema>;

/** The largest limit `GET /api/signals` accepts. Asking for more is clamped. */
export const SIGNALS_MAX = 5000;

/**
 * The widest window `GET /api/signals` accepts, which it validates with
 * `.max(365)` and rejects rather than truncates. Asking for more is clamped
 * here, so `?sessions=400` loads a year of sessions instead of turning the
 * whole feed into "returned 400".
 */
export const SIGNALS_MAX_SESSIONS = 365;

/**
 * The default window: the newest session that produced a signal.
 *
 * Counted in sessions rather than in calendar days, so a Monday shows Friday
 * and a holiday does not blank the feed — "today" here is the last session
 * that fired anything, not the calendar date. One is the read a trader makes
 * first, what fired today, and it is small enough that the whole window fits
 * inside one fetch, which is what stops the feed truncating a family away.
 * The 5-session and All buttons are how yesterday comes back.
 */
export const SIGNALS_SESSIONS = 1;

/**
 * Every session, spelled as a number because the URL carries numbers here.
 *
 * Zero rather than an absent parameter: absent already means "the default",
 * and a bookmark of the all-sessions view has to survive a reload.
 */
export const SIGNALS_ALL_SESSIONS = 0;

/**
 * The symbol workspace's deep link.
 *
 * `section` is an arbitrary string here on purpose. Whether it names a real
 * section is checked against `SectionId` at the page; the schema cannot import
 * it without coupling this whole module to the symbol page.
 */
export const symbolSearchSchema = z.object({ section: z.string().optional() }).strip();
export type SymbolSearch = z.infer<typeof symbolSearchSchema>;

/**
 * A YYYY-MM-DD that names a real day.
 *
 * Mirrors `parseSessionDate` in `@savi/core`, which `GET /api/strategy-lab`
 * runs on the same fields and answers 400 when it throws. Checking here means
 * a hand-edited `?from=2026-02-30` falls back to the run's own bound instead
 * of turning the whole page into "returned 400".
 */
function isRealDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

const labDate = z.string().refine(isRealDate).optional().catch(undefined);

/**
 * A bounded number that reads a blank `?minTurnoverCr=` as absent, as the
 * route's `blankIsAbsent` does. `z.coerce.number` reads "" and null as 0, and
 * the page would send that 0 in place of the route's default.
 */
const labNumber = (schema: z.ZodNumber) =>
  z
    .preprocess((value) => (value === "" || value === null ? undefined : value), schema.optional())
    .catch(undefined);

/**
 * The strategy lab's URL: every control on the page, the selected stock, and
 * the stock table's sort.
 *
 * Every enum, number, and date falls back to undefined rather than failing,
 * for the reason the `from`/`to` comment on `signalsSearchSchema` gives: a
 * bookmark must still load the page. Undefined means "the server's default",
 * which `STRATEGY_LAB_DEFAULTS` spells out. The number bounds are the route's
 * own (`apps/api/src/query/strategy-lab/schema.ts`), so a value the route
 * would refuse becomes the default here instead. An unknown strategy, such as
 * one renamed since the bookmark was saved, becomes the default strategy for
 * the same reason; the ids are `LAB_STRATEGY_IDS`, held to core's by a test.
 */
export const strategyLabSearchSchema = z
  .object({
    strategy: z.enum(LAB_STRATEGY_IDS).optional().catch(undefined),
    from: labDate,
    to: labDate,
    split: labDate,
    exit: z.enum(["own", "hold21"]).optional().catch(undefined),
    universe: z.enum(["liquid", "nifty500"]).optional().catch(undefined),
    minTurnoverCr: labNumber(z.coerce.number().min(0).max(10_000)),
    minTrades: labNumber(z.coerce.number().int().min(1).max(500)),
    group: z
      .enum(["sector", "size", "volatility", "trend", "liquidity"])
      .optional()
      .catch(undefined),
    // The selected stock, and the stock table's sort: page state, never sent
    // to GET /api/strategy-lab.
    symbol: z.string().optional(),
    sort: z.string().optional(),
    dir: z.enum(["asc", "desc"]).optional().catch(undefined),
  })
  .strip();
export type StrategyLabSearch = z.infer<typeof strategyLabSearchSchema>;

/**
 * What an absent strategy-lab parameter means: the route's own defaults. The
 * cache key, the pressed chips, and the page all read them here, so the three
 * cannot disagree about what "no `?minTrades=`" asks for.
 */
export const STRATEGY_LAB_DEFAULTS = {
  strategy: "prophet_swing",
  exit: "own",
  universe: "liquid",
  minTurnoverCr: 5,
  minTrades: 20,
  group: "sector",
} as const;
